import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useGoogleLogin } from "@react-oauth/google";
import { listDriveFolders, shareFolder } from "../../shared/services/GoogleDriveService";
import { CONFIG } from "../../shared/config/constants";
import { authHeaders } from "../../shared/utils/authHelpers";
import ReactConfetti from "react-confetti";

function DriveConnectButton({
  onConnect,
  onFolderSelect = () => { },
  autoStart = false,
  caseId = "",
  onError = null,
}) {
  const API_BASE = CONFIG.app.pdfApiUrl;
  const [folders, setFolders] = useState([]);
  const [token, setToken] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [shared, setShared] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [shareError, setShareError] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);

  const driveLogin = useGoogleLogin({
    scope:
      "https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/drive.metadata.readonly https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile openid",
    onSuccess: async (res) => {
      setToken(res.access_token);
      const driveFolders = await listDriveFolders(res.access_token);
      setFolders(driveFolders);
      if (onConnect) onConnect(res.access_token);
    },
    onError: (err) => console.error("❌ Drive login failed:", err),
  });

  useEffect(() => {
    if (autoStart && !token) {
      driveLogin();
    }
  }, [autoStart, token, driveLogin]);

  // 🔹 MAIN ENTRY
  const handleShare = async () => {
    if (!selectedFolder) {
      if (onError) {
        onError("Please select a folder first.");
      }
      return;
    }
    setIsSharing(true);
    try {
      await shareFolder(token, selectedFolder, CONFIG.app.underwriterEmail);
      setShared(true);
      setShareError("");
      const allFiles = await fetchAllFilesRecursively(token, selectedFolder);
      const processingResult = await sendFilesForProcessing(token, selectedFolder, allFiles);
      setShowConfetti(true); // Trigger confetti on success
      return processingResult;
    } catch (error) {
      console.error("❌ Failed to generate summary:", error);
      setShareError("Unable to send files. Please try again.");
    } finally {
      setIsSharing(false);
    }
  };

  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 5000); // Show confetti for 5 seconds
      return () => clearTimeout(timer);
    }
  }, [showConfetti]);

  // 🔹 RECURSIVE FETCH
  async function fetchAllFilesRecursively(token, folderId, allFiles = []) {
    const url = `https://www.googleapis.com/drive/v3/files?q='${folderId}'+in+parents+and+trashed=false&fields=files(id,name,mimeType)`;

    // SECURITY: Never pass tokens in URL! Use Authorization header instead
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      }
    });

    if (!response.ok) throw new Error("Unable to load folder contents. Please try again.");
    const data = await response.json();

    for (const file of data.files) {
      if (file.mimeType === "application/vnd.google-apps.folder") {
        await fetchAllFilesRecursively(token, file.id, allFiles);
      } else {
        allFiles.push(file);
      }
    }
    return allFiles;
  }

  // 🔹 SEND FILE LIST TO BACKEND FOR PROCESSING
  async function sendFilesForProcessing(token, folderId, files) {
    const headers = {
      "Content-Type": "application/json",
      accept: "application/json",
      ...authHeaders(),
    };
    if (!headers.Authorization) {
      throw new Error("Unable to continue. Please sign in and try again.");
    }
    if (!caseId) {
      throw new Error("Please enter your case number to continue.");
    }
    const payload = {
      token,
      folderId,
      caseId: caseId,
      details: {
        folderId,
        fileCount: files.length,
        requestedAt: new Date().toISOString(),
      },
      files: files.map((file) => ({
        id: file.id,
        name: file.name,
        mimeType: file.mimeType,
      })),
    };
    try {
      const response = await fetch(`${API_BASE}/process-drive-files`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Unable to process your request. Please try again.");
      }

      const data = await response.json();
      if (data?.status === "accepted") {
        return data;
      }
      throw new Error("Unable to process your request. Please try again.");
    } catch (error) {
      console.error("❌ Failed to send files for processing:", error);
      throw error;
    }
  }


  return (
    <div style={{ marginTop: 30 }}>
      {!token ? (
        <button
          onClick={() => driveLogin()}
          style={{
            background: "linear-gradient(90deg, #3b82f6, #2563eb)",
            color: "white",
            padding: "10px 18px",
            border: "none",
            borderRadius: "10px",
            cursor: "pointer",
            fontWeight: 600,
            boxShadow: "0 10px 30px rgba(59,130,246,0.35)",
          }}
        >
          Connect to Google Drive
        </button>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              gap: "18px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <select
              onChange={(e) => {
                setSelectedFolder(e.target.value);
                onFolderSelect(e.target.value);
              }}
              style={{
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid rgba(148,163,184,0.4)",
                minWidth: "240px",
                background: "rgba(30, 41, 59, 0.8)",
                color: "#e2e8f0",
                outline: "none"
              }}
            >
              <option value="" style={{ color: "#94a3b8" }}>Select a folder</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id} style={{ color: "#e2e8f0", background: "#1e293b" }}>
                  {f.name}
                </option>
              ))}
            </select>

            <button
              onClick={handleShare}
              disabled={!selectedFolder || isSharing}
              style={{
                background: "linear-gradient(90deg, #3b82f6, #2563eb)",
                color: "white",
                padding: "10px 20px",
                border: "none",
                borderRadius: "8px",
                cursor: isSharing || !selectedFolder ? "not-allowed" : "pointer",
                opacity: isSharing || !selectedFolder ? 0.7 : 1,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: 600,
                boxShadow: isSharing ? "none" : "0 4px 12px rgba(37,99,235,0.4)"
              }}
            >
              {isSharing ? (
                <>
                  <span className="animate-spin" style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.6)", borderTopColor: "transparent", borderRadius: "9999px", display: "inline-block" }} />
                  Sharing…
                </>
              ) : (
                "Share & Process"
              )}
            </button>
          </div>

          {shared && (
            <div className="animate-scale-in" style={{ marginTop: "16px", padding: "12px", background: "rgba(22, 163, 74, 0.1)", borderRadius: "10px", border: "1px solid rgba(22, 163, 74, 0.2)" }}>
              <p style={{ color: "#4ade80", margin: 0, fontWeight: 600 }}>
                ✅ Your documents are shared with our underwriter!
              </p>
              <p style={{ color: "#bbf7d0", fontSize: "12px", margin: "4px 0 0" }}>
                We will contact you to follow up in 48 hours.
              </p>
            </div>
          )}
          {isSharing && (
            <p style={{ color: "#93c5fd", marginTop: "8px", fontSize: "13px" }}>
              Sharing your folder and preparing files…
            </p>
          )}
          {shareError && (
            <p style={{ color: "#f87171", marginTop: "6px", fontSize: "12px" }}>
              {shareError}
            </p>
          )}
        </>
      )}
      {showConfetti && <ReactConfetti numberOfPieces={200} recycle={false} gravity={0.3} />}
    </div>
  );
}

DriveConnectButton.propTypes = {
  onConnect: PropTypes.func,
  onFolderSelect: PropTypes.func,
  autoStart: PropTypes.bool,
  caseId: PropTypes.string,
  onError: PropTypes.func,
};

export default DriveConnectButton;
